// Vector overlay that draws press-grade production marks on top of a proof.
// All marks scale to the actual product dimensions (inches) so what the
// customer sees on screen matches the press output 1:1.
//
//   - Trim / cut line   :  solid navy line at the exact label silhouette
//   - Bleed             :  outer dashed line, 0.125" beyond trim
//   - Safety margin     :  inner dashed line, 0.125" inside trim
//   - Crop marks        :  short corner ticks 0.125" outside the bleed
//   - Centerline        :  faint cross-hairs to verify alignment
//
// Designed to overlay any image (proof preview) — it sits absolute over
// the parent and matches the aspect ratio of the label artwork.
//
// Rendering is delegated to focused sub-components in ./marks.jsx so that
// this file stays a thin composition layer.
import {
  BleedMark, TrimMark, SafetyMark, CenterCrosshairs, CropMarks, LegendChip,
} from "./marks";

function buildGeometry({ widthIn, heightIn, bleedIn }) {
  const margin = bleedIn + 0.25;                    // 0.25" breathing room for crop marks
  const vbW = widthIn + 2 * margin;
  const vbH = heightIn + 2 * margin;
  const originX = margin;
  const originY = margin;
  const cx = originX + widthIn / 2;
  const cy = originY + heightIn / 2;

  const bleedX = originX - bleedIn;
  const bleedY = originY - bleedIn;
  const bleedW = widthIn + 2 * bleedIn;
  const bleedH = heightIn + 2 * bleedIn;

  // 4 corners of the bleed box — used for crop marks.
  const corners = [
    { x: bleedX,            y: bleedY,            isLeft: true,  isTop: true,  id: "tl" },
    { x: bleedX + bleedW,   y: bleedY,            isLeft: false, isTop: true,  id: "tr" },
    { x: bleedX,            y: bleedY + bleedH,   isLeft: true,  isTop: false, id: "bl" },
    { x: bleedX + bleedW,   y: bleedY + bleedH,   isLeft: false, isTop: false, id: "br" },
  ];

  return { vbW, vbH, originX, originY, cx, cy, corners };
}

function normaliseShape(shape) {
  const s = (shape || "").toLowerCase();
  if (s === "circle") return "circle";
  if (s === "oval") return "oval";
  return "rect";
}

export default function ProductionMarks({
  widthIn = 3,
  heightIn = 2,
  bleedIn = 0.125,
  safetyIn = 0.125,
  show = true,
  shape = "Rectangle",
}) {
  if (!show) return null;

  const shapeKey = normaliseShape(shape);
  const { vbW, vbH, originX, originY, cx, cy, corners } = buildGeometry({ widthIn, heightIn, bleedIn });

  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      preserveAspectRatio="xMidYMid meet"
      className="absolute inset-0 w-full h-full pointer-events-none"
      data-testid="production-marks-overlay"
    >
      <BleedMark shape={shapeKey} cx={cx} cy={cy} w={widthIn} h={heightIn} bleedIn={bleedIn} />
      <TrimMark  shape={shapeKey} cx={cx} cy={cy} w={widthIn} h={heightIn} />
      <SafetyMark shape={shapeKey} cx={cx} cy={cy} w={widthIn} h={heightIn} safetyIn={safetyIn} />
      <CenterCrosshairs originX={originX} originY={originY} w={widthIn} h={heightIn} />
      <CropMarks corners={corners} />
      <LegendChip widthIn={widthIn} heightIn={heightIn} vbW={vbW} vbH={vbH} />
    </svg>
  );
}
